import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface EstoqueFormProps {
  initialData?: any;
  categoriasDisponiveis: string[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  title: string;
}

export function EstoqueForm({ initialData, categoriasDisponiveis, onSubmit, onCancel, title }: EstoqueFormProps) {
  const [formData, setFormData] = useState({
    nome: initialData?.nome || "",
    marca: initialData?.marca || "",
    categoria: initialData?.categoria || categoriasDisponiveis[0] || "Outros",
    tipo_controle: initialData?.tipo_controle || "Por unidade",
    quantidade_atual: initialData?.quantidade_atual || "",
    quantidade_minima: initialData?.quantidade_minima || "",
    nivel_atual: initialData?.nivel_atual || "Alto",
    alerta_nivel: initialData?.alerta_nivel || "Baixo",
    observacoes: initialData?.observacoes || "",
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const niveis = ["Alto", "Médio", "Baixo", "Crítico"];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-lg mx-auto shadow-2xl animate-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-white/5">
          <X size={24} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Nome do Produto *</Label>
          <Input 
            value={formData.nome}
            onChange={(e) => handleChange("nome", e.target.value)}
            placeholder="Ex: Agulhas 3RL, Tinta Preta"
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Marca</Label>
            <Input 
              value={formData.marca}
              onChange={(e) => handleChange("marca", e.target.value)}
              placeholder="Ex: Dynamic, Electric Ink"
              className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Categoria</Label>
            <Select value={formData.categoria} onValueChange={(v) => handleChange("categoria", v)}>
              <SelectTrigger className="bg-background border-border h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                {categoriasDisponiveis.map(cat => (
                  <SelectItem key={cat} value={cat} className="focus:bg-primary focus:text-white">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Tipo de Controle</Label>
          <div className="flex bg-background p-1 rounded-lg border border-border">
            <button
              type="button"
              onClick={() => handleChange("tipo_controle", "Por unidade")}
              className={cn(
                "flex-1 py-2 text-xs font-semibold rounded-md transition-all",
                formData.tipo_controle === "Por unidade" ? "bg-primary text-white" : "text-muted-foreground hover:text-zinc-300"
              )}
            >
              Por unidade
            </button>
            <button
              type="button"
              onClick={() => handleChange("tipo_controle", "Por nível")}
              className={cn(
                "flex-1 py-2 text-xs font-semibold rounded-md transition-all",
                formData.tipo_controle === "Por nível" ? "bg-primary text-white" : "text-muted-foreground hover:text-zinc-300"
              )}
            >
              Por nível
            </button>
          </div>
        </div>

        {formData.tipo_controle === "Por unidade" ? (
          <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-sm font-medium">Quantidade Atual</Label>
              <Input 
                type="number"
                value={formData.quantidade_atual}
                onChange={(e) => handleChange("quantidade_atual", e.target.value)}
                placeholder="0"
                className="bg-background border-border focus-visible:border-primary h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-sm font-medium">Quantidade Mínima</Label>
              <Input 
                type="number"
                value={formData.quantidade_minima}
                onChange={(e) => handleChange("quantidade_minima", e.target.value)}
                placeholder="Ex: Alerta com 5"
                className="bg-background border-border focus-visible:border-primary h-11"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-sm font-medium">Nível Atual</Label>
              <Select value={formData.nivel_atual} onValueChange={(v) => handleChange("nivel_atual", v)}>
                <SelectTrigger className="bg-background border-border h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {niveis.map(n => (
                    <SelectItem key={n} value={n} className="focus:bg-primary focus:text-white">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-sm font-medium">Alerta a partir de</Label>
              <Select value={formData.alerta_nivel} onValueChange={(v) => handleChange("alerta_nivel", v)}>
                <SelectTrigger className="bg-background border-border h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {niveis.slice(1).map(n => (
                    <SelectItem key={n} value={n} className="focus:bg-primary focus:text-white">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Observações</Label>
          <Textarea 
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            placeholder="Informações sobre o produto..."
            className="bg-background border-border min-h-[80px] resize-none"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12">
            Salvar Produto
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="px-6 border-border bg-transparent text-[#A1A1AA] h-12">
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
